import { useEffect, useState } from "react";
import { Globe, Store, Copy, Check, MousePointerClick, ShoppingBag, Clock, CheckCircle2, IndianRupee, Wallet, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { PageHeader, StatCard, StatusBadge, LoadingRows, TableShell, TR, TD, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";

export default function CreatorSiteAffiliate() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");

  const load = () => api.get("/vid/creator/site-affiliate").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const join = async () => {
    setBusy(true);
    try { await api.post("/vid/creator/site-affiliate/join"); toast.success("Website & Store Affiliate program joined!"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const copy = async (key, path) => {
    try { await navigator.clipboard.writeText(`${window.location.origin}${path}`); } catch { /* ignore */ }
    setCopied(key); toast.success("Affiliate link copied!"); setTimeout(() => setCopied(""), 2000);
  };

  if (!data) return <LoadingRows rows={4} />;

  if (!data.joined) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 space-y-5" data-testid="site-affiliate-join">
        <div className="mx-auto h-16 w-16 rounded-2xl flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB)" }}>
          <Share2 className="h-8 w-8" />
        </div>
        <h1 className="font-display font-extrabold text-2xl tracking-tight">Website & Store Affiliate Program</h1>
        <p className="text-sm text-muted-foreground">Refer businesses to create their website or store on MILTROO. When their payment is verified and approved, your commission lands in your existing creator earnings — withdrawable from one place.</p>
        <Button data-testid="site-affiliate-join-btn" onClick={join} disabled={busy} className="rounded-full font-bold text-white border-0 px-8 h-12" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join & Get My Universal Link"}
        </Button>
      </div>
    );
  }

  const s = data.stats;
  return (
    <div className="space-y-8" data-testid="site-affiliate-page">
      <PageHeader title="Website & Store Affiliate" subtitle="Share your link to help businesses create a website or online store and earn commission on approved orders." testid="site-affiliate-title" />

      <div className="max-w-2xl">
        <div className="rounded-2xl p-5 text-white" style={{ background: "linear-gradient(135deg,#04150e,#0a1f14)", border: "1px solid rgba(34,197,94,.3)" }} data-testid="sa-link-card-universal">
          <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-1.5"><Share2 className="h-4 w-4" />Universal Affiliate Link</p>
          <code className="block bg-black/30 rounded-xl px-3 py-2.5 text-xs font-mono mt-3 truncate" data-testid="sa-link-universal">{window.location.origin}{data.link}</code>
          <Button onClick={() => copy("universal", data.link)} data-testid="sa-copy-universal" className="mt-3 rounded-full font-bold text-[#04150e] border-0 h-9" style={{ background: "linear-gradient(135deg,#4ade80,#16a34a)" }}>
            {copied === "universal" ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}{copied === "universal" ? "Copied!" : "Copy Link"}
          </Button>
          <p className="text-[11px] text-emerald-200/60 mt-3">One link for both — customers choose Website or Online Store after clicking. Your commission is calculated from their selection.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={MousePointerClick} label="Link Clicks" value={s.clicks} testid="sa-stat-clicks" tint="bg-blue-500/10 text-blue-500" />
        <StatCard icon={Globe} label="Orders" value={s.orders} testid="sa-stat-orders" delay={0.05} tint="bg-emerald-500/10 text-emerald-500" />
        <StatCard icon={Clock} label="Pending Commission" value={fmtINR(s.pending_commissions)} testid="sa-stat-pending" delay={0.15} tint="bg-amber-500/10 text-amber-500" />
        <StatCard icon={CheckCircle2} label="Approved Commission" value={fmtINR(s.approved_commissions)} testid="sa-stat-approved" delay={0.2} tint="bg-emerald-500/10 text-emerald-500" />
        <StatCard icon={IndianRupee} label="Total Affiliate Earnings" value={fmtINR(s.total_earnings)} testid="sa-stat-total" delay={0.25} tint="bg-pink-500/10 text-pink-500" />
        <StatCard icon={Wallet} label="Withdrawable Balance" value={fmtINR(s.withdrawable)} testid="sa-stat-withdrawable" delay={0.3} tint="bg-teal-500/10 text-teal-500" />
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3">Affiliate Order History</h2>
        {data.orders.length === 0 ? <EmptyState title="No orders yet" body="Share your universal link on YouTube, Instagram Reels or WhatsApp to get started." /> : (
          <TableShell head={["Business", "Type", "Price", "Status", "Date"]} testid="sa-orders-table">
            {data.orders.map((o) => (
              <TR key={o.id} testid={`sa-order-${o.id}`}>
                <TD><span className="font-semibold text-sm">{o.business_name}</span></TD>
                <TD><span className="text-xs font-bold capitalize">{o.kind}</span></TD>
                <TD>{fmtINR(o.price)}</TD>
                <TD><StatusBadge status={o.status} /></TD>
                <TD><span className="text-xs text-muted-foreground">{fmtDateTime(o.created_at)}</span></TD>
              </TR>
            ))}
          </TableShell>
        )}
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3">Commission Transactions</h2>
        {data.commissions.length === 0 ? <EmptyState title="No commissions yet" body="Commissions appear here after orders are approved." /> : (
          <TableShell head={["Order", "Sale Value", "Commission", "Status", "Date"]} testid="sa-commissions-table">
            {data.commissions.map((c) => (
              <TR key={c.id} testid={`sa-comm-${c.id}`}>
                <TD><span className="font-semibold text-sm">{c.offer_name}</span></TD>
                <TD>{fmtINR(c.sale_value)}</TD>
                <TD><span className="font-bold text-emerald-600">{fmtINR(c.commission)}</span></TD>
                <TD><StatusBadge status={c.status} /></TD>
                <TD><span className="text-xs text-muted-foreground">{fmtDateTime(c.created_at)}</span></TD>
              </TR>
            ))}
          </TableShell>
        )}
      </div>
      <p className="text-xs text-muted-foreground">Approved commissions combine into your main <a href="/creator/earnings" className="font-bold text-primary underline">Earnings & Withdraw</a> balance — one wallet, one withdrawal.</p>
    </div>
  );
}
